/* PAWSNOVA blind-box campaign, 2026-10-09. Scoped UI; Shopify owns pricing. */
(() => {
  if (window.pawsBBExperience) return;
  window.pawsBBExperience = { version: '20261009-v1' };
  const pageURL = new URL(document.baseURI);
  const preview = pageURL.searchParams.get('preview_theme_id');
  const withPreview = (url) => {
    const result = new URL(url, document.baseURI);
    if (preview && result.origin === pageURL.origin) result.searchParams.set('preview_theme_id', preview);
    return result;
  };
  document.addEventListener('click', (event) => {
    const link = event.target.closest?.('a[data-paws-bb-link]');
    if (link) link.href = withPreview(link.href).toString();
  });

  class PawsBBProductNotice extends HTMLElement {
    connectedCallback() {
      if (this.bound) return;
      this.bound = true;
      this.eligible = new Set((this.dataset.eligibleIds || '').split(',').filter(Boolean));
      this.owner = this.closest('product-info') || this.closest('[id^="MainProduct-"]');
      this.onChange = (event) => {
        const form = document.getElementById(this.dataset.formId);
        if (event.target.form === form || this.owner?.contains(event.target)) {
          if (event.target.name === 'id' || event.target.name === 'selling_plan') this.sync();
          else if (event.target.closest('variant-selects, variant-radios')) this.hidden = true;
        }
      };
      document.addEventListener('change', this.onChange);
      this.unsubscribers = [];
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        if (PUB_SUB_EVENTS.variantChange) this.unsubscribers.push(subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          const data = event?.data || {};
          if (String(data.sectionId) !== String(this.dataset.sectionId)) return;
          this.sync(data.variant);
        }));
        if (PUB_SUB_EVENTS.optionValueSelectionChange) this.unsubscribers.push(subscribe(PUB_SUB_EVENTS.optionValueSelectionChange, (event) => {
          const target = event?.data?.event?.target;
          if (target && this.owner?.contains(target)) this.hidden = true;
        }));
      }
      this.sync();
    }
    sync(variant) {
      const form = document.getElementById(this.dataset.formId);
      const input = form?.querySelector('input[name="id"]');
      const variantId = String(variant?.id ?? input?.value ?? '');
      const subscription = Array.from(form?.querySelectorAll('[name="selling_plan"]') || [])
        .some((node) => !node.disabled && (!/radio|checkbox/.test(node.type) || node.checked) && !!node.value);
      this.hidden = !this.eligible.has(variantId) || !!input?.disabled || variant?.available === false || subscription;
    }
    disconnectedCallback() {
      document.removeEventListener('change', this.onChange);
      this.unsubscribers?.forEach((fn) => fn?.());
      this.bound = false;
    }
  }
  if (!customElements.get('paws-bb-product-notice')) customElements.define('paws-bb-product-notice', PawsBBProductNotice);

  class PawsBBShop extends HTMLElement {
    connectedCallback() {
      if (this.bound) return;
      this.bound = true;
      this.adding = false;
      this.revision = 0;
      this.onSubmit = (event) => {
        const form = event.target.closest?.('[data-paws-bb-form]');
        if (!form || !this.contains(form)) return;
        event.preventDefault();
        event.stopPropagation();
        if (!this.adding) this.add(form);
      };
      this.schedule = (event) => {
        if (this.adding || event?.source === 'paws-bb-shop' || event?.detail?.source === 'paws-bb-shop') return;
        clearTimeout(this.timer);
        this.timer = setTimeout(() => this.refreshProgress().catch(() => {}), 120);
      };
      this.addEventListener('submit', this.onSubmit);
      document.addEventListener('cart:updated', this.schedule);
      document.addEventListener('paws:cart-data', this.schedule);
      this.onPageShow = (event) => { if (event.persisted) this.schedule(); };
      window.addEventListener('pageshow', this.onPageShow);
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, this.schedule);
      }
      this.refreshProgress().catch(() => {});
    }
    disconnectedCallback() {
      clearTimeout(this.timer);
      this.progressController?.abort();
      this.unsubscribe?.();
      this.removeEventListener('submit', this.onSubmit);
      document.removeEventListener('cart:updated', this.schedule);
      document.removeEventListener('paws:cart-data', this.schedule);
      window.removeEventListener('pageshow', this.onPageShow);
      this.bound = false;
    }
    feedback(message) {
      const node = this.querySelector('[data-paws-bb-feedback]');
      if (node) node.textContent = message;
    }
    useProgress(html) {
      if (typeof html !== 'string' || !this.isConnected) return false;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fresh = doc.querySelector('[data-paws-bb-progress]');
      const current = this.querySelector('[data-paws-bb-progress]');
      if (!fresh || !current) return false;
      const hadFocus = current.contains(document.activeElement);
      current.replaceWith(fresh);
      if (hadFocus) fresh.querySelector('a')?.focus({ preventScroll: true });
      return true;
    }
    async refreshProgress() {
      const requestRevision = ++this.revision;
      this.progressController?.abort();
      const controller = new AbortController();
      this.progressController = controller;
      const url = withPreview(this.dataset.cartUrl || '/cart');
      url.searchParams.set('section_id', this.dataset.progressSection || 'paws-bb-progress');
      const response = await fetch(url.toString(), {
        credentials: 'same-origin', cache: 'no-store', signal: controller.signal,
        headers: { Accept: 'text/html' }
      });
      if (!response.ok) throw new Error('Progress unavailable');
      const html = await response.text();
      if (requestRevision !== this.revision || !this.isConnected) return;
      if (!this.useProgress(html)) throw new Error('Progress unavailable');
    }
    updateCartSections(sections) {
      if (!sections) return;
      const iconHTML = sections['cart-icon-bubble'];
      if (typeof iconHTML === 'string') {
        const doc = new DOMParser().parseFromString(iconHTML, 'text/html');
        const icon = doc.querySelector('#shopify-section-cart-icon-bubble') || doc.querySelector('.shopify-section');
        const target = document.getElementById('cart-icon-bubble');
        if (icon && target) target.innerHTML = icon.innerHTML;
      }
      const drawerHTML = sections['cart-drawer'];
      if (typeof drawerHTML === 'string') {
        const doc = new DOMParser().parseFromString(drawerHTML, 'text/html');
        const source = doc.getElementById('CartDrawer');
        const target = document.getElementById('CartDrawer');
        const drawer = document.querySelector('cart-drawer');
        if (source && target && drawer && !drawer.classList.contains('active')) {
          target.innerHTML = source.innerHTML;
          drawer.classList.remove('is-empty');
          target.querySelector('#CartDrawer-Overlay')?.addEventListener('click', () => drawer.close?.());
          // Deliberately do not call renderContents() or open(): keep browsing uninterrupted.
        }
      }
    }
    async add(form) {
      const variantId = form.querySelector('[name="id"]')?.value;
      if (!/^\d+$/.test(variantId || '')) return;
      this.adding = true;
      clearTimeout(this.timer);
      this.progressController?.abort();
      this.revision += 1;
      const buttons = Array.from(this.querySelectorAll('[data-paws-bb-form] button'));
      const disabledBefore = buttons.map((button) => button.disabled);
      buttons.forEach((button) => { button.disabled = true; });
      const button = form.querySelector('button');
      const previousLabel = button?.textContent || '+ Add';
      if (button) button.textContent = 'Adding…';
      this.feedback('');
      let added = false;
      try {
        const endpoint = new URL(form.action);
        if (!endpoint.pathname.endsWith('.js')) endpoint.pathname += '.js';
        const sectionsURL = withPreview(this.dataset.cartUrl || '/cart');
        const response = await fetch(endpoint.toString(), {
          method: 'POST', credentials: 'same-origin',
          headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
          body: JSON.stringify({
            items: [{ id: variantId, quantity: 1 }],
            sections: ['cart-icon-bubble', 'cart-drawer', 'paws-bb-progress'],
            sections_url: sectionsURL.pathname + sectionsURL.search
          })
        });
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(payload.description || payload.message || 'Could not add this item.');
        added = true;
        this.updateCartSections(payload.sections);
        if (!this.useProgress(payload.sections?.['paws-bb-progress'])) {
          await this.refreshProgress().catch(() => this.feedback('Added. View your cart to check the latest discount.'));
        }
        const root = window.Shopify?.routes?.root || '/';
        let cart = null;
        try {
          const cartResponse = await fetch(`${root}cart.js`, { credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' } });
          if (cartResponse.ok) cart = await cartResponse.json();
        } catch (_) { /* Addition succeeded; never repeat the POST to recover a totals refresh. */ }
        if (!this.querySelector('[data-paws-bb-feedback]')?.textContent) this.feedback('Added to your cart. Keep choosing or view your cart.');
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
          try {
            Promise.resolve(publish(PUB_SUB_EVENTS.cartUpdate, { source: 'paws-bb-shop', productVariantId: variantId, cartData: cart }))
              .catch(() => {});
          } catch (_) { /* Other theme subscribers must not turn a successful add into a failure. */ }
        }
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { source: 'paws-bb-shop', cart } }));
        if (cart && Array.isArray(cart.items)) document.dispatchEvent(new CustomEvent('paws:cart-data', { detail: { source: 'paws-bb-shop', cart } }));
      } catch (error) {
        if (added) this.feedback('Added. View your cart to check the latest discount.');
        else if (error instanceof TypeError) this.feedback('We could not confirm the update. Check your cart before trying again.');
        else this.feedback(error.message || 'Could not add this item. Please check your cart.');
      } finally {
        this.adding = false;
        buttons.forEach((node, index) => { node.disabled = disabledBefore[index]; });
        if (button) button.textContent = previousLabel;
      }
    }
  }
  if (!customElements.get('paws-bb-shop')) customElements.define('paws-bb-shop', PawsBBShop);
})();
